import React, { useCallback, useEffect, useState } from 'react';

import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import HelpGuideModal from '@components/HelpGuideModal';
import ReportItDialog from '@components/ReportItDialog';

import MainScreen from './MainScreen';
import Settings from './Settings';
import SettingsProfile from './Settings/SettingsProfile';

import { IUserProfileModal, ProfileScreensEnum } from './interfaces';
import { useOpenConfirmLogoutModal } from '@components/ConfirmLogoutModal';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat/context';
import { useModalCTAContext } from '@providers/ModalCTAProvider';

/**
 * Renders the profile drawer and the dialogs its menu opens (help guide, Report
 * an Issue), keeping the dialogs outside the drawer so they survive its close.
 *
 * @param props.isOpen - Whether the drawer is open.
 * @param props.onClose - Toggles the drawer closed.
 */
const UserProfileModal = ({ onClose, isOpen }: IUserProfileModal) => {
  const [activeScreen, setActiveScreen] = useState<ProfileScreensEnum>(
    ProfileScreensEnum.DEFAULT,
  );
  const [helpOpen, setHelpOpen] = useState(false);
  // Both dialogs live here rather than in MainScreen: the drawer runs with
  // keepMounted: false, so opening one from a menu item and closing the menu
  // would unmount the dialog with it (MV-1895).
  const [reportOpen, setReportOpen] = useState(false);
  const { setValuesForModal } = useModalCTAContext();

  const {
    open: isOpenedConfirmLogoutModal,
    openModal: onOpenLogoutModal,
    closeModal: onCloseConfirmLogoutModal,
  } = useOpenConfirmLogoutModal();

  const eventContext = useMytaverseEvent();
  const cleanStates = eventContext?.cleanStates;
  const { resetState } = useChatState();

  useEffect(() => {
    if (!isOpen) {
      setActiveScreen(ProfileScreensEnum.DEFAULT);
    }
  }, [isOpen]);

  const render = () => {
    switch (activeScreen) {
      case ProfileScreensEnum.SETTINGS:
        return <Settings onClose={onClose} setActiveScreen={setActiveScreen} />;
      case ProfileScreensEnum.SETTINGS_LANGUAGE:
        return (
          <SettingsProfile
            onClose={onClose}
            setActiveScreen={setActiveScreen}
          />
        );
      case ProfileScreensEnum.DEFAULT:
        return (
          <MainScreen
            onClose={onClose}
            setActiveScreen={setActiveScreen}
            onOpenLogoutModal={onOpenLogoutModal}
            onOpenHelpModal={() => setHelpOpen(true)}
            onOpenReportModal={() => setReportOpen(true)}
          />
        );
    }
  };

  const handleOnConfirmLogout = useCallback(() => {
    cleanStates?.();
    resetState();
  }, [cleanStates, resetState]);

  useEffect(() => {
    setValuesForModal({
      open: isOpenedConfirmLogoutModal,
      altStyling: true,
      onClose: onCloseConfirmLogoutModal,
      onConfirm: handleOnConfirmLogout,
      isConfirmLogoutModal: true,
    });
  }, [
    isOpenedConfirmLogoutModal,
    setValuesForModal,
    onCloseConfirmLogoutModal,
    handleOnConfirmLogout,
  ]);

  return (
    <>
      <SwipeableDrawer
        position={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        onClose={onClose}
        onOpen={onClose}
        sx={{ '& .MuiDrawer-paper': { zIndex: 10002 } }}
        // On the Modal root the id lands on a zero-height positioning wrapper
        // that sits below the viewport, so Playwright never sees it as visible
        // even once the drawer is open. The paper is the panel itself.
        PaperProps={
          {
            'data-testid': 'user-profile-modal',
            sx: {
              backgroundColor: 'rgba(12, 11, 11, 0.86)',
              backdropFilter: 'blur(24px)',
            },
          } as never
        }
      >
        {render()}
      </SwipeableDrawer>
      <HelpGuideModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ReportItDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </>
  );
};

export default UserProfileModal;
